import React, { useState, useCallback } from 'react';
import { useFormBuilderState } from '../hooks/useFormBuilderState';
import type { FormBuilderService, FormBuilderStoreState, LocalQuestion, QuestionType } from '../types';
import { QUESTION_TYPES } from '../types';
import SurveyDetailsForm from './SurveyDetailsForm';
import AIAssistPanel from './AIAssistPanel';
import QuestionsList from './QuestionsList';
import FormPreview from './FormPreview';
import QuestionModal from './QuestionModal';
import ErrorDisplay from '@/Components/common/ErrorDisplay';
import '@/pages/Admin/FeedbackManagement/components/FormBuilder.css';

interface FormBuilderProps {
  service: FormBuilderService & FormBuilderStoreState;
  hasAIAssist?: boolean;
}

const getTypeLabel = (type: QuestionType): string =>
  QUESTION_TYPES.find(t => t.value === type)?.label || type;

const FormBuilder: React.FC<FormBuilderProps> = ({ service, hasAIAssist = false }) => {
  const state = useFormBuilderState(service);
  const [showModal, setShowModal] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState<LocalQuestion | null>(null);

  const handleAddQuestion = useCallback(() => {
    setEditingQuestion(null);
    setShowModal(true);
  }, []);

  const handleEditQuestion = useCallback((question: LocalQuestion) => {
    setEditingQuestion(question);
    setShowModal(true);
  }, []);

  const handleDeleteQuestion = useCallback((id: string) => {
    state.setQuestions(prev => prev.filter(q => q.id !== id));
  }, [state]);

  const handleSaveQuestion = useCallback((question: LocalQuestion) => {
    if (editingQuestion) {
      state.setQuestions(prev => prev.map(q => q.id === editingQuestion.id ? question : q));
    } else {
      state.setQuestions(prev => [...prev, question]);
    }
    setShowModal(false);
    setEditingQuestion(null);
  }, [editingQuestion, state]);

  const handleCloseModal = useCallback(() => {
    setShowModal(false);
    setEditingQuestion(null);
  }, []);

  const handleReorderQuestions = useCallback((reordered: LocalQuestion[]) => {
    state.setQuestions(reordered);
  }, [state]);

  const handleAddAIQuestions = useCallback((newQuestions: LocalQuestion[]) => {
    state.setQuestions(prev => [...prev, ...newQuestions]);
  }, [state]);

  return (
    <div className="admin-feedback-form-builder">
      {state.storeError && (
        <ErrorDisplay
          title="Error"
          message={state.storeError}
          type="error"
          showRetry={false}
          showClose={true}
          onClose={() => window.location.reload()}
        />
      )}

      <div className="admin-feedback-form-builder-layout">
        <div className="admin-feedback-builder-main">
          <SurveyDetailsForm
            editingFormId={state.editingFormId}
            formName={state.formName}
            setFormName={state.setFormName}
            formDescription={state.formDescription}
            setFormDescription={state.setFormDescription}
            formType={state.formType}
            setFormType={state.setFormType}
            assessmentType={state.assessmentType}
            setAssessmentType={state.setAssessmentType}
            expiryDate={state.expiryDate}
            setExpiryDate={state.setExpiryDate}
            maxResponses={state.maxResponses}
            setMaxResponses={state.setMaxResponses}
            onePerBrowser={state.onePerBrowser}
            setOnePerBrowser={state.setOnePerBrowser}
            collectName={state.collectName}
            setCollectName={state.setCollectName}
            collectEmail={state.collectEmail}
            setCollectEmail={state.setCollectEmail}
            showCorrectAnswers={state.showCorrectAnswers}
            setShowCorrectAnswers={state.setShowCorrectAnswers}
            showReport={state.showReport}
            setShowReport={state.setShowReport}
            objectiveRatio={state.objectiveRatio}
            setObjectiveRatio={state.setObjectiveRatio}
          />

          {hasAIAssist && state.formType === 'assessment' && (
            <AIAssistPanel
              assessmentType={state.assessmentType}
              objectiveRatio={state.objectiveRatio}
              onAddQuestions={handleAddAIQuestions}
            />
          )}

          <QuestionsList
            questions={state.questions}
            onAdd={handleAddQuestion}
            onEdit={handleEditQuestion}
            onDelete={handleDeleteQuestion}
            onReorder={handleReorderQuestions}
            onSaveDraft={state.handleSaveDraft}
            onPublish={state.handlePublish}
            saving={state.saving}
            getTypeLabel={getTypeLabel}
            formType={state.formType}
            assessmentType={state.assessmentType}
            objectiveRatio={state.objectiveRatio}
          />
        </div>

        <FormPreview
          formName={state.formName}
          formDescription={state.formDescription}
          collectName={state.collectName}
          collectEmail={state.collectEmail}
          questions={state.questions}
        />
      </div>

      <QuestionModal
        isOpen={showModal}
        editingQuestion={editingQuestion}
        formType={state.formType}
        assessmentType={state.assessmentType}
        availableTypes={state.getAvailableQuestionTypes()}
        onSave={handleSaveQuestion}
        onClose={handleCloseModal}
      />
    </div>
  );
};

export default FormBuilder;
